import { isAffiliate, isVendor } from '@/authProvider'
import Info from '@/icons/info.svg?react'
import Graph from '@/icons/graph.svg?react'
import { handleCsvDownload } from '@/utils/handleCsvDownloads'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import {
  Datagrid,
  DateField,
  FunctionField,
  Link,
  List,
  Loading,
  NumberField,
  required,
  SelectInput,
  TextField,
  TextInput,
  useCreatePath
} from 'react-admin'
import useSales from '../hooks/useSales'
import { SalesData } from '../storage/SalesProvider'
import { ParsedSale } from '../types/ParsedObjects'
import SaleNameField from './SaleNameField'

type SaleRecord = {
  lineItemsCount: number
  refundedLineItemsCount: number
}

const affiliateFilters = (
  data: SalesData,
  setCurrentPublisher: (id: number) => void
) => {
  return [
    <SelectInput
      key="publisher"
      source="publisherId"
      label="Promote deals from"
      validate={required()}
      choices={data.publishers}
      onChange={(e) => setCurrentPublisher(e.target.value)}
      alwaysOn
    />
  ]
}

const SaleList = () => {
  const { loading, data, setCurrentPublisher } = useSales()
  const createPath = useCreatePath()

  let filters: Array<JSX.Element> = []

  if (!loading) {
    if (isAffiliate()) {
      filters = affiliateFilters(data, setCurrentPublisher)
    }

    filters.push(<TextInput key="search" label="Name" source="name" alwaysOn />)
    filters.push(
      <SelectInput
        key="status"
        source="status"
        label="Status"
        validate={required()}
        choices={[
          { id: 'active', name: 'Active' },
          { id: 'completed', name: 'Completed' },
          { id: 'upcoming', name: 'Upcoming' }
        ]}
        alwaysOn
      />
    )
  }

  if (loading) return <Loading />

  const dateOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  } as Intl.DateTimeFormatOptions

  return (
    <List
      filters={filters}
      filterDefaultValues={{ publisherId: data.publisherId, status: 'active' }}
      sort={{ field: 'lineItemsCount', order: 'DESC' }}
      sx={{ pb: '50px' }}
    >
      <Datagrid rowClick={false} bulkActionButtons={false} size="medium">
        <TextField source="id" label="ID" sortable={false} />
        {isVendor() && (
          <TextField source="vendorSku" label="Vendor SKU" sortable={false} />
        )}
        {isVendor() && (
          <FunctionField
            label="CSV"
            render={(sale: ParsedSale) => {
              const productId = sale.products[0].databaseId.toString()
              const productName = sale.products[0].name

              return (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCsvDownload(productId, productName)}
                  sx={{ textTransform: 'none' }}
                >
                  Download
                </Button>
              )
            }}
          />
        )}
        <SaleNameField
          source="name"
          sx={{ display: 'block', maxWidth: '430px' }}
        />

        {!isVendor() && (
          <NumberField
            source="priceInCents"
            label="Price"
            textAlign="left"
            options={{ style: 'currency', currency: 'USD' }}
            transform={(v: number) => v / 100}
          />
        )}
        <DateField source="activeAt" label="Start" options={dateOptions} />
        <DateField source="expiresAt" label="End" options={dateOptions} />
        <NumberField source="lineItemsCount" label="Total Sold" />
        <NumberField source="refundedLineItemsCount" label="Refunds" />
        <FunctionField
          render={(record: SaleRecord) =>
            record.lineItemsCount - record.refundedLineItemsCount
          }
          sortBy="netLineItemsCount"
          label="Net Sold"
          textAlign="right"
        />
        {isVendor() && (
          <FunctionField
            label="Inventory"
            render={(sale: ParsedSale) => (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {sale.maxAvailable - sale.numSold}/{sale.maxAvailable}
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: '50px',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: 'primary.main',
                      height: '12px',
                      width: `${((sale.maxAvailable - sale.numSold) / sale.maxAvailable) * 100}%`,
                      boxSizing: 'inherit'
                    }}
                  />
                </Box>
              </Box>
            )}
          />
        )}

        <FunctionField
          label={
            <Tooltip title="Click graph icon for sale details.">
              <IconButton>
                <Info width="20px" height="20px" />
              </IconButton>
            </Tooltip>
          }
          render={(sale: ParsedSale) => (
            <Link
              to={createPath({
                resource: 'sales',
                type: 'show',
                id: sale.id
              })}
            >
              <Graph height="25px" />
            </Link>
          )}
        />
      </Datagrid>
    </List>
  )
}

export default SaleList
